import React, { useState, useEffect, useCallback, useMemo } from "react";
import API from "../../config/api";
import { useAuth } from "../../hooks/useAuth";

export default function AdminDashboard() {
  const { getToken } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [updating, setUpdating] = useState({});
  const [toast, setToast]       = useState("");
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [sortBy, setSortBy]     = useState("email");
  const [sortDir, setSortDir]   = useState("asc");
  const [page, setPage]         = useState(1);
  const PAGE_SIZE = 20;

  const headers = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  }), [getToken]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 4000);
  };

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API.adminStats, { headers: headers() });
      const d = await res.json();
      if (res.ok) setData(d);
      else showToast(`❌ ${d.error}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const updateSub = async (targetEmail, newType, resetUsage = false) => {
    setUpdating(p => ({ ...p, [targetEmail]: true }));
    try {
      const res = await fetch(API.adminUpdateSubscription, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ target_email: targetEmail, subscription_type: newType, reset_usage: resetUsage }),
      });
      const d = await res.json();
      if (d.success) { showToast(`✅ ${d.message}`); loadStats(); }
      else showToast(`❌ ${d.error}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setUpdating(p => ({ ...p, [targetEmail]: false })); }
  };

  // Filter + search + sort
  const processed = useMemo(() => {
    if (!data?.subscribers) return [];
    let rows = [...data.subscribers];

    // Filter
    if (filter !== "all") rows = rows.filter(s => s.subscription_type === filter);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(s =>
        s.email.toLowerCase().includes(q) ||
        (s.name || "").toLowerCase().includes(q) ||
        (s.mobile || "").includes(q) ||
        (s.company_name || "").toLowerCase().includes(q) ||
        (s.twilio_number || "").includes(q)
      );
    }

    // Sort
    rows.sort((a, b) => {
      let va, vb;
      if (sortBy === "calls_left")    { va = a.usage.calls.left;    vb = b.usage.calls.left; }
      else if (sortBy === "campaign_left") { va = a.usage.campaign.left; vb = b.usage.campaign.left; }
      else if (sortBy === "sub")      { va = a.subscription_type;   vb = b.subscription_type; }
      else if (sortBy === "created")  { va = a.created_at;          vb = b.created_at; }
      else                            { va = a[sortBy] || "";        vb = b[sortBy] || ""; }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  }, [data, filter, search, sortBy, sortDir]);

  const totalPages = Math.ceil(processed.length / PAGE_SIZE);
  const pageRows   = processed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
    setPage(1);
  };

  const SortIcon = ({ col }) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅";

  const UsagePill = ({ used, limit }) => {
    const left = Math.max(0, limit - used);
    const pct  = limit > 0 ? (used / limit) * 100 : 0;
    const color = pct >= 100 ? "#f85149" : pct >= 80 ? "#f0883e" : "#3fb950";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ width: 48, background: "#21262d", borderRadius: 4, height: 5 }}>
          <div style={{ width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, height: 5 }} />
        </div>
        <span style={{ color, fontSize: 11, fontWeight: "bold", minWidth: 28 }}>{left}</span>
        <span style={{ color: "#484f58", fontSize: 10 }}>/{limit}</span>
      </div>
    );
  };

  return (
    <div style={S.wrap}>
      {/* Toast */}
      {toast && (
        <div style={{ ...S.toast, background: toast.startsWith("✅") ? "#1a3a1a" : "#3a1a1a",
          borderColor: toast.startsWith("✅") ? "#3fb950" : "#f85149" }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={{ color: "#e6edf3", margin: 0 }}>👥 Subscriber Management</h2>
          {data && (
            <div style={{ color: "#8b949e", fontSize: 13, marginTop: 4 }}>
              Total: <b style={{ color: "#e6edf3" }}>{data.summary.total}</b> &nbsp;|&nbsp;
              Pro: <b style={{ color: "#FF9900" }}>{data.summary.pro}</b> &nbsp;|&nbsp;
              Free: <b style={{ color: "#8b949e" }}>{data.summary.free}</b> &nbsp;|&nbsp;
              Showing: <b style={{ color: "#58a6ff" }}>{processed.length}</b>
            </div>
          )}
        </div>
        <button style={S.refreshBtn} onClick={loadStats}>🔄 Refresh</button>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        {/* Search */}
        <input
          style={S.search}
          placeholder="🔍  Search by email, name, mobile, company, Twilio number..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 6 }}>
          {[["all", "All"], ["pro", "⭐ Pro"], ["free", "Free"]].map(([val, label]) => (
            <button key={val}
              style={{ ...S.filterBtn, ...(filter === val ? S.filterActive : {}) }}
              onClick={() => { setFilter(val); setPage(1); }}>
              {label} {data ? `(${val === "all" ? data.summary.total : val === "pro" ? data.summary.pro : data.summary.free})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ color: "#8b949e", padding: 60, textAlign: "center", fontSize: 14 }}>
          Loading subscribers...
        </div>
      ) : (
        <>
          {/* Table */}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr style={S.thead}>
                  <th style={S.th} onClick={() => toggleSort("email")}>Email <SortIcon col="email" /></th>
                  <th style={S.th} onClick={() => toggleSort("name")}>Name <SortIcon col="name" /></th>
                  <th style={S.th}>Mobile</th>
                  <th style={S.th} onClick={() => toggleSort("company_name")}>Company <SortIcon col="company_name" /></th>
                  <th style={S.th}>Twilio #</th>
                  <th style={S.th} onClick={() => toggleSort("sub")}>Tier <SortIcon col="sub" /></th>
                  <th style={S.th} onClick={() => toggleSort("calls_left")}>Calls Left <SortIcon col="calls_left" /></th>
                  <th style={S.th} onClick={() => toggleSort("campaign_left")}>Campaign Left <SortIcon col="campaign_left" /></th>
                  <th style={S.th}>KB Left</th>
                  <th style={S.th} onClick={() => toggleSort("created")}>Joined <SortIcon col="created" /></th>
                  <th style={{ ...S.th, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={11} style={{ color: "#8b949e", textAlign: "center", padding: 40 }}>No subscribers found</td></tr>
                ) : pageRows.map((s, i) => (
                  <tr key={s.email} style={{ ...S.tr, background: i % 2 === 0 ? "#0d1117" : "#161b22" }}>
                    <td style={S.td}>
                      <span style={{ color: "#58a6ff", fontSize: 13 }}>{s.email}</span>
                    </td>
                    <td style={S.td}><span style={{ color: "#e6edf3" }}>{s.name || "-"}</span></td>
                    <td style={S.td}><span style={{ color: "#8b949e", fontSize: 12 }}>{s.mobile || "-"}</span></td>
                    <td style={S.td}><span style={{ color: "#e6edf3", fontSize: 12 }}>{s.company_name || "-"}</span></td>
                    <td style={S.td}><span style={{ color: "#8b949e", fontSize: 12 }}>{s.twilio_number || "-"}</span></td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <span style={{
                        padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: "bold",
                        background: s.subscription_type === "pro" ? "#FF990022" : "#21262d",
                        color: s.subscription_type === "pro" ? "#FF9900" : "#8b949e",
                        border: `1px solid ${s.subscription_type === "pro" ? "#FF9900" : "#30363d"}`,
                      }}>
                        {s.subscription_type.toUpperCase()}
                      </span>
                    </td>
                    <td style={S.td}><UsagePill used={s.usage.calls.used}    limit={s.usage.calls.limit} /></td>
                    <td style={S.td}><UsagePill used={s.usage.campaign.used} limit={s.usage.campaign.limit} /></td>
                    <td style={S.td}><UsagePill used={s.usage.kb.used}       limit={s.usage.kb.limit} /></td>
                    <td style={S.td}>
                      <span style={{ color: "#8b949e", fontSize: 11 }}>
                        {s.created_at ? new Date(s.created_at * 1000).toLocaleDateString("en-IN") : "-"}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                        {s.subscription_type === "free" ? (
                          <button style={S.btnUpgrade} disabled={updating[s.email]}
                            onClick={() => updateSub(s.email, "pro")}>
                            {updating[s.email] ? "..." : "⬆ Pro"}
                          </button>
                        ) : (
                          <button style={S.btnDowngrade} disabled={updating[s.email]}
                            onClick={() => updateSub(s.email, "free")}>
                            {updating[s.email] ? "..." : "⬇ Free"}
                          </button>
                        )}
                        <button style={S.btnReset} disabled={updating[s.email]}
                          title="Reset usage counters"
                          onClick={() => {
                            if (window.confirm(`Reset usage for ${s.email}?`))
                              updateSub(s.email, s.subscription_type, true);
                          }}>
                          {updating[s.email] ? "..." : "↺"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={S.pagination}>
              <span style={{ color: "#8b949e", fontSize: 13 }}>
                Page {page} of {totalPages} &nbsp;({processed.length} results)
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <button style={S.pageBtn} disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button style={S.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  return (
                    <button key={p} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}
                      onClick={() => setPage(p)}>{p}</button>
                  );
                })}
                <button style={S.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button style={S.pageBtn} disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const S = {
  wrap:        { padding: "24px 32px", background: "#0d1117", minHeight: "100vh" },
  toast:       { position: "fixed", top: 16, right: 16, padding: "10px 20px", borderRadius: 8, border: "1px solid", fontSize: 13, zIndex: 9999, color: "#e6edf3" },
  header:      { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  refreshBtn:  { padding: "8px 16px", background: "#21262d", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", cursor: "pointer", fontSize: 13 },
  toolbar:     { display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" },
  search:      { flex: 1, minWidth: 300, padding: "8px 14px", background: "#161b22", border: "1px solid #30363d", borderRadius: 8, color: "#e6edf3", fontSize: 13, outline: "none" },
  filterBtn:   { padding: "6px 14px", background: "#21262d", border: "1px solid #30363d", borderRadius: 8, color: "#8b949e", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" },
  filterActive:{ background: "#1f6feb22", borderColor: "#1f6feb", color: "#58a6ff" },
  tableWrap:   { overflowX: "auto", borderRadius: 10, border: "1px solid #30363d" },
  table:       { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  thead:       { background: "#161b22" },
  th:          { padding: "10px 14px", color: "#8b949e", fontWeight: 600, textAlign: "left", borderBottom: "1px solid #30363d", whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", fontSize: 12 },
  tr:          { borderBottom: "1px solid #21262d" },
  td:          { padding: "10px 14px", verticalAlign: "middle", whiteSpace: "nowrap" },
  btnUpgrade:  { padding: "4px 10px", background: "#FF990022", border: "1px solid #FF9900", borderRadius: 6, color: "#FF9900", cursor: "pointer", fontSize: 11, fontWeight: "bold" },
  btnDowngrade:{ padding: "4px 10px", background: "#21262d", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", cursor: "pointer", fontSize: 11 },
  btnReset:    { padding: "4px 8px", background: "#1f6feb11", border: "1px solid #1f6feb44", borderRadius: 6, color: "#58a6ff", cursor: "pointer", fontSize: 13, title: "Reset usage" },
  pagination:  { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, padding: "0 4px" },
  pageBtn:     { padding: "5px 10px", background: "#21262d", border: "1px solid #30363d", borderRadius: 6, color: "#8b949e", cursor: "pointer", fontSize: 12 },
  pageBtnActive:{ background: "#1f6feb", borderColor: "#1f6feb", color: "#fff" },
};
